/**
 * Net-cost calc module for the "bikPlusFixedTradeup" mode (BCH).
 *
 * Logic moved verbatim from ratebook.js (MR 272). All behaviour is unchanged.
 * Calc modules import ratebook.js for shared primitives; this is safe because
 * cross-references only happen inside function bodies, never at module-evaluation time.
 */

import Ratebook from '@/helpers/ratebook';

export const CALC_NAME = 'bikPlusFixedTradeup';

/** Marker string searched for (case-insensitive) in the driver grade NAME field. */
const tradeupGradeMarker = '[TRADE UP]';

/**
 * Fixed illustrative Salary Sacrifice tradeup figures, keyed by income-tax band %.
 * B = grossSalarySacrifice, C = incomeTaxSaved, D = nationalInsuranceSaved, E = netCost (B-C-D).
 * C and D are stored as positive magnitudes; templates render them with a leading "-".
 *
 * @type {{ [rate: number]: { grossSalarySacrifice: number, incomeTaxSaved: number, nationalInsuranceSaved: number, netCost: number } }}
 */
const fixedSalarySacrificeTradeup = {
    20: { grossSalarySacrifice: 100, incomeTaxSaved: 20, nationalInsuranceSaved: 8,  netCost: 72 },
    40: { grossSalarySacrifice: 100, incomeTaxSaved: 40, nationalInsuranceSaved: 2,  netCost: 58 },
    45: { grossSalarySacrifice: 100, incomeTaxSaved: 45, nationalInsuranceSaved: 2,  netCost: 53 },
};

/**
 * Returns true when the BCH product has the bikPlusFixedTradeup net-cost display mode enabled.
 *
 * @param {object} adminPortalProduct
 * @returns {boolean}
 */
export function isEnabled(adminPortalProduct) {
    return adminPortalProduct?.calcs_net_cost_display === CALC_NAME;
}

/**
 * Returns true when the driver grade name contains the "[TRADE UP]" marker (case-insensitive).
 * Detection is on the grade NAME field, not friendlyName.
 *
 * @param {string|null} driverGradeName
 * @returns {boolean}
 */
export function isTradeupGrade(driverGradeName) {
    return !!driverGradeName && driverGradeName.toUpperCase().includes(tradeupGradeMarker);
}

/**
 * Returns the fixed SS tradeup row for the given income-tax band rate, or null for unrecognised bands.
 *
 * @param {number} ratePercent - e.g. 20, 40, or 45.
 * @returns {{ grossSalarySacrifice: number, incomeTaxSaved: number, nationalInsuranceSaved: number, netCost: number }|null}
 */
export function tradeupRow(ratePercent) {
    return fixedSalarySacrificeTradeup[ratePercent] ?? null;
}

/**
 * Returns the net cost (E) of the SS tradeup for the given tax band, or 0 for unrecognised bands.
 *
 * @param {number} ratePercent
 * @returns {number}
 */
export function tradeupNetCost(ratePercent) {
    return tradeupRow(ratePercent)?.netCost ?? 0;
}

/**
 * Returns the net cost figure for the bikPlusFixedTradeup mode for use in the ratebook dispatcher.
 *
 * @param {object} ratebook
 * @param {number} term
 * @param {{ ratePercent?: number, driverGradeName?: string }} [context={}]
 * @returns {number}
 */
export function netCost(ratebook, term, context = {}) {
    if (isTradeupGrade(context.driverGradeName)) {
        // [TRADE UP] grade: F = A + E
        return Number(Ratebook.averageMonthlyBik(ratebook, term))
             + Number(tradeupNetCost(context.ratePercent));
    }
    // Non-[TRADE UP] grade under this mode: use bikTaxAverage (decision #4)
    return Number(ratebook.bikTaxAverage);
}

export default { CALC_NAME, isEnabled, isTradeupGrade, tradeupRow, tradeupNetCost, netCost };
